/* Admin · Wallets & Payouts — Mission Control for every seller's money
 *
 * Surfaces wallet balances, transaction velocity, and auto-payout schedule
 * state across the entire platform. Click any row → opens Account 360
 * drawer focused on the Wallet tab (reuses the component shipped earlier).
 *
 * Backend: GET /api/admin/seller-wallets · GET /api/admin/payout-runs
 *          POST /api/admin/payouts/run (manual scheduler fire).
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Search, Wallet, ArrowDownRight, ArrowUpRight, CalendarClock,
  Sparkles, Loader2, Play, ScrollText, RefreshCw, CheckCircle2,
  XCircle, AlertTriangle, CreditCard, Download, FileText, History, Plus, Trash2, Star,
} from "lucide-react";
import AdminAccount360Drawer from "../../components/AdminAccount360Drawer";

function money(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n === "object") n = n.amount ?? n.value ?? null;
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return v < 0 ? `-$${abs}` : `$${abs}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const ROLE_FILTERS = [
  { v: "all",    l: "All wallets" },
  { v: "seller", l: "Sellers" },
  { v: "buyer",  l: "Buyers" },
  { v: "auto",   l: "Auto-payout ON" },
];

const RUN_FILTERS = [
  { v: "",        l: "All decisions" },
  { v: "paid",    l: "Paid" },
  { v: "skipped", l: "Skipped" },
  { v: "failed",  l: "Failed" },
];

const DECISION_TONE = {
  paid:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  skipped: "bg-amber-50 text-amber-800 border-amber-200",
  failed:  "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminWallets() {
  const [walletsData, setWalletsData] = useState(null);
  const [runsData, setRunsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("wallets");   // wallets | runs
  const [roleFilter, setRoleFilter] = useState("all");
  const [decisionFilter, setDecisionFilter] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedUserForDrawer, setSelectedUserForDrawer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [w, r] = await Promise.all([
        api.get("/admin/seller-wallets"),
        api.get("/admin/payout-runs"),
      ]);
      setWalletsData(w.data);
      setRunsData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load wallets");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runScheduler = async () => {
    if (!window.confirm("Fire the auto-payout scheduler now? This evaluates every enabled schedule and writes audit rows.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/run");
      toast.success(`Scheduler ran · ${data.paid} paid · ${data.skipped} skipped · ${data.failed} failed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally { setBusy(false); }
  };

  const visibleWallets = useMemo(() => {
    if (!walletsData) return [];
    let rows = walletsData.rows || [];
    if (roleFilter === "seller") rows = rows.filter(r => r.role === "seller");
    if (roleFilter === "buyer")  rows = rows.filter(r => r.role === "buyer");
    if (roleFilter === "auto")   rows = rows.filter(r => r.payout_schedule?.enabled);
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.email || "").toLowerCase().includes(n) ||
        (r.company || "").toLowerCase().includes(n) ||
        (r.name || "").toLowerCase().includes(n)
      );
    }
    return rows;
  }, [walletsData, roleFilter, q]);

  const visibleRuns = useMemo(() => {
    if (!runsData) return [];
    let rows = runsData.items || [];
    if (decisionFilter) rows = rows.filter(r => r.decision === decisionFilter);
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      rows = rows.filter(r =>
        ((r.user || {}).email || "").toLowerCase().includes(n) ||
        ((r.user || {}).company || "").toLowerCase().includes(n) ||
        (r.reason || "").toLowerCase().includes(n)
      );
    }
    return rows;
  }, [runsData, decisionFilter, q]);

  const summary = walletsData?.summary || {};
  const runsSummary = runsData?.summary || {};

  return (
    <>
      <PageHeader
        title="Wallets · Mission Control"
        subtitle={loading
          ? "Loading…"
          : `${summary.wallets_total || 0} wallets · ${money(summary.balance_held_total)} held · ${money(summary.credited_7d)} in · ${money(summary.debited_7d)} out (7d) · ${summary.auto_payout_enabled || 0} on auto-payout`}/>

      <div className="p-8 space-y-5" data-testid="admin-wallets-page">
        {/* KPI tiles */}
        <div className="grid md:grid-cols-5 gap-3" data-testid="admin-wallets-tiles">
          <KpiTile l="Balance held" v={money(summary.balance_held_total)} icon={Wallet}/>
          <KpiTile l="Credited · 7d" v={money(summary.credited_7d)} icon={ArrowDownRight} tone="emerald"/>
          <KpiTile l="Debited · 7d" v={money(summary.debited_7d)} icon={ArrowUpRight} tone="rose"/>
          <KpiTile l="Net · 7d" v={money(summary.net_7d)} icon={Wallet}
            tone={(summary.net_7d || 0) >= 0 ? "emerald" : "rose"}/>
          <KpiTile l="Auto-payout ON" v={summary.auto_payout_enabled ?? "—"} icon={CalendarClock}/>
        </div>

        {/* View switcher + Run-now */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-1.5 flex-wrap">
            <ViewToggle active={view === "wallets"} onClick={() => setView("wallets")} testid="admin-wallets-view-wallets">
              Wallets
            </ViewToggle>
            <ViewToggle active={view === "runs"} onClick={() => setView("runs")} testid="admin-wallets-view-runs">
              Payout runs <span className="ml-1 mono text-[10.5px] opacity-65">{runsSummary.total ?? "—"}</span>
            </ViewToggle>
            <ViewToggle active={view === "methods"} onClick={() => setView("methods")} testid="admin-wallets-view-methods">
              Payment methods
            </ViewToggle>
            <ViewToggle active={view === "topups"} onClick={() => setView("topups")} testid="admin-wallets-view-topups">
              Topups
            </ViewToggle>
            <ViewToggle active={view === "withdrawals"} onClick={() => setView("withdrawals")} testid="admin-wallets-view-withdrawals">
              Withdrawals
            </ViewToggle>
            <ViewToggle active={view === "statements"} onClick={() => setView("statements")} testid="admin-wallets-view-statements">
              Statements
            </ViewToggle>
            <ViewToggle active={view === "lifecycle"} onClick={() => setView("lifecycle")} testid="admin-wallets-view-lifecycle">
              Lifecycle log
            </ViewToggle>
            <ViewToggle active={view === "lender-wallets"} onClick={() => setView("lender-wallets")} testid="admin-wallets-view-lender-wallets">
              Lender wallets
            </ViewToggle>
            <ViewToggle active={view === "lender-txns"} onClick={() => setView("lender-txns")} testid="admin-wallets-view-lender-txns">
              Lender txns
            </ViewToggle>
            <ViewToggle active={view === "lender-policies"} onClick={() => setView("lender-policies")} testid="admin-wallets-view-lender-policies">
              Lender policies
            </ViewToggle>
            <ViewToggle active={view === "grant-proposals"} onClick={() => setView("grant-proposals")} testid="admin-wallets-view-grant-proposals">
              Credit-grant proposals
            </ViewToggle>
            <ViewToggle active={view === "loans-roster"} onClick={() => setView("loans-roster")} testid="admin-wallets-view-loans-roster">
              Loans roster
            </ViewToggle>
            <ViewToggle active={view === "cards"} onClick={() => setView("cards")} testid="admin-wallets-view-cards">
              Cards
            </ViewToggle>
            <ViewToggle active={view === "multi-pay"} onClick={() => setView("multi-pay")} testid="admin-wallets-view-multi-pay">
              Multi-pay batches
            </ViewToggle>
            <ViewToggle active={view === "credit-apps"} onClick={() => setView("credit-apps")} testid="admin-wallets-view-credit-apps">
              Credit applications
            </ViewToggle>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={runScheduler} disabled={busy}
              data-testid="admin-wallets-run-scheduler"
              className="h-9 rounded-full bg-foreground text-background hover:opacity-90 text-[12px]">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Play className="w-3.5 h-3.5 mr-1.5"/>}
              Run scheduler now
            </Button>
            <Button onClick={load} variant="outline" size="sm" className="h-9 rounded-full text-[12px]"
              data-testid="admin-wallets-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh
            </Button>
          </div>
        </div>

        {/* Filters (only for wallets / runs views — new tabs have their own filters) */}
        {(view === "wallets" || view === "runs") && (
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex flex-wrap gap-1.5">
              {(view === "wallets" ? ROLE_FILTERS : RUN_FILTERS).map(f => {
                const active = view === "wallets" ? roleFilter === f.v : decisionFilter === f.v;
                return (
                  <button key={f.v} onClick={() => view === "wallets" ? setRoleFilter(f.v) : setDecisionFilter(f.v)}
                    data-testid={`admin-wallets-filter-${f.v || "all"}`}
                    className={`text-[12px] rounded-full px-3.5 py-1.5 border transition-all ${
                      active
                        ? "bg-foreground text-background border-foreground"
                        : "bg-white border-border text-foreground/65 hover:border-foreground/40"
                    }`}>
                    {f.l}
                  </button>
                );
              })}
            </div>
            <div className="relative w-72">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
              <Input value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search by name, email, company…"
                className="pl-9 h-9 rounded-full bg-white border-border text-[12.5px]"
                data-testid="admin-wallets-search"/>
            </div>
          </div>
        )}

        {/* Body */}
        {loading && (view === "wallets" || view === "runs") ? (
          <div className="rounded-2xl bg-white border border-border p-12 text-center text-foreground/55">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading…
          </div>
        ) : view === "wallets" ? (
          <WalletsTable rows={visibleWallets} onOpen={setSelectedUserForDrawer}/>
        ) : view === "runs" ? (
          <PayoutRunsTable rows={visibleRuns} summary={runsSummary}/>
        ) : view === "methods" ? (
          <MethodsView/>
        ) : view === "topups" ? (
          <TopupsView/>
        ) : view === "withdrawals" ? (
          <WithdrawalsView/>
        ) : view === "statements" ? (
          <StatementsView/>
        ) : view === "lifecycle" ? (
          <LifecycleView/>
        ) : view === "lender-wallets" ? (
          <LenderWalletsView/>
        ) : view === "lender-txns" ? (
          <LenderTxnsView/>
        ) : view === "lender-policies" ? (
          <LenderPoliciesView/>
        ) : view === "grant-proposals" ? (
          <GrantProposalsView/>
        ) : view === "loans-roster" ? (
          <LoansRosterView/>
        ) : view === "cards" ? (
          <CardsView/>
        ) : view === "multi-pay" ? (
          <MultiPayBatchesView/>
        ) : view === "credit-apps" ? (
          <CreditApplicationsView/>
        ) : null}
      </div>

      <AdminAccount360Drawer
        user={selectedUserForDrawer}
        onClose={() => setSelectedUserForDrawer(null)}
      />
    </>
  );
}

function KpiTile({ l, v, icon: Icon, tone }) {
  const toneCls = {
    emerald: "text-emerald-700",
    rose:    "text-rose-700",
  }[tone] || "text-foreground/85";
  return (
    <div className="rounded-2xl bg-white border border-border p-5">
      <div className="flex items-center gap-2 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
        <Icon className="w-3.5 h-3.5"/>{l}
      </div>
      <div className={`display text-2xl mt-1.5 ${toneCls}`}>{v}</div>
    </div>
  );
}

function ViewToggle({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm border transition-colors ${
        active
          ? "bg-foreground text-background border-foreground"
          : "bg-white text-foreground/75 border-border hover:border-foreground/30"
      }`}>
      {children}
    </button>
  );
}

function WalletsTable({ rows, onOpen }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-border p-12 text-center text-foreground/55">
        No wallets match.
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="admin-wallets-table">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
          <tr>
            <th className="px-5 py-3 font-medium">User</th>
            <th className="px-5 py-3 font-medium">Role</th>
            <th className="px-5 py-3 font-medium text-right">Balance</th>
            <th className="px-5 py-3 font-medium text-right">In · 30d</th>
            <th className="px-5 py-3 font-medium text-right">Out · 30d</th>
            <th className="px-5 py-3 font-medium text-right">Topup · 30d</th>
            <th className="px-5 py-3 font-medium text-right">Payout · 30d</th>
            <th className="px-5 py-3 font-medium">Auto-payout</th>
            <th className="px-5 py-3 font-medium">Last activity</th>
            <th className="px-5 py-3"/>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(r => (
            <tr key={r.user_id} data-testid={`admin-wallet-row-${r.user_id.slice(0, 8)}`}
              className="hover:bg-secondary/40 cursor-pointer transition-colors"
              onClick={() => onOpen?.({ id: r.user_id, name: r.name, email: r.email, company: r.company, role: r.role })}>
              <td className="px-5 py-3">
                <div className="font-medium">{r.company || r.name}</div>
                <div className="text-[11px] text-foreground/55 mono">{r.email}</div>
              </td>
              <td className="px-5 py-3"><span className="chip bg-foreground text-background text-[10px]">{r.role}</span></td>
              <td className="px-5 py-3 mono text-right font-semibold">{money(r.balance)}</td>
              <td className="px-5 py-3 mono text-right text-emerald-700">{money(r.in_30d)}</td>
              <td className="px-5 py-3 mono text-right text-rose-700">{money(r.out_30d)}</td>
              <td className="px-5 py-3 mono text-right text-foreground/65">{money(r.topup_30d)}</td>
              <td className="px-5 py-3 mono text-right text-foreground/65">{money(r.payout_30d)}</td>
              <td className="px-5 py-3">
                {r.payout_schedule?.enabled
                  ? <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10.5px] inline-flex items-center gap-1">
                      <CheckCircle2 className="w-2.5 h-2.5"/>{r.payout_schedule.frequency || "on"}
                    </span>
                  : <span className="chip border border-border text-foreground/55 text-[10.5px]">manual</span>}
              </td>
              <td className="px-5 py-3 text-[11.5px] text-foreground/55 mono">{fmtDate(r.last_activity_at).split(",")[0] || "—"}</td>
              <td className="px-5 py-3 text-right">
                <span className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-widest text-foreground/60">
                  <Sparkles className="w-3 h-3 text-accent"/>360 →
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayoutRunsTable({ rows, summary }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-border p-12 text-center text-foreground/55" data-testid="admin-payout-runs-empty">
        <ScrollText className="w-6 h-6 mx-auto mb-2 opacity-40"/>
        No scheduler runs captured yet.
        <p className="text-[11.5px] text-foreground/45 mt-2">Click "Run scheduler now" to fire it manually and start the audit trail.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-white border border-border p-4 grid grid-cols-4 gap-4 text-center" data-testid="admin-payout-runs-summary">
        <KpiInline l="Total runs" v={summary.total ?? 0}/>
        <KpiInline l="Paid" v={summary.paid ?? 0} tone="emerald" icon={CheckCircle2}/>
        <KpiInline l="Skipped" v={summary.skipped ?? 0} tone="amber" icon={AlertTriangle}/>
        <KpiInline l="Failed" v={summary.failed ?? 0} tone="rose" icon={XCircle}/>
      </div>
      <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="admin-payout-runs-table">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
            <tr>
              <th className="px-5 py-3 font-medium">Run at</th>
              <th className="px-5 py-3 font-medium">User</th>
              <th className="px-5 py-3 font-medium">Decision</th>
              <th className="px-5 py-3 font-medium">Reason</th>
              <th className="px-5 py-3 font-medium text-right">Amount</th>
              <th className="px-5 py-3 font-medium text-right">Balance before</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.id} data-testid={`admin-payout-run-${r.id.slice(0, 8)}`}>
                <td className="px-5 py-3 text-[11.5px] text-foreground/65 mono">{fmtDate(r.run_at)}</td>
                <td className="px-5 py-3">
                  <div className="font-medium">{(r.user || {}).company || (r.user || {}).name || "—"}</div>
                  <div className="text-[11px] text-foreground/55 mono">{(r.user || {}).email}</div>
                </td>
                <td className="px-5 py-3">
                  <span className={`chip border text-[10.5px] inline-flex items-center gap-1 ${DECISION_TONE[r.decision] || "bg-secondary border-border"}`}>
                    {r.decision}
                  </span>
                </td>
                <td className="px-5 py-3 text-[12.5px] text-foreground/75">
                  {r.reason || "—"}
                  {r.error && <div className="text-[11px] text-rose-600 mt-0.5">{r.error}</div>}
                </td>
                <td className="px-5 py-3 mono text-right">{money(r.amount)}</td>
                <td className="px-5 py-3 mono text-right text-foreground/55">{money(r.balance_before)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiInline({ l, v, tone, icon: Icon }) {
  const toneCls = {
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    rose:    "text-rose-700",
  }[tone] || "text-foreground/85";
  return (
    <div>
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 inline-flex items-center gap-1 justify-center">
        {Icon && <Icon className="w-3 h-3"/>}{l}
      </div>
      <div className={`display text-2xl ${toneCls} mt-1`}>{v}</div>
    </div>
  );
}


/* ═══ Iter-85: Payments visibility sub-tabs ════════════════════════════ */
function MethodsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(365);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (type) p.set("method_type", type);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/payment-methods/list?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-pm-list">
      <KpiRow>
        <KpiSm l="Methods" v={data?.kpis?.total_count ?? 0} icon={CreditCard}/>
        <KpiSm l="Verified" v={data?.kpis?.verified ?? 0} tone="emerald" icon={CheckCircle2}/>
        <KpiSm l="Unverified" v={data?.kpis?.unverified ?? 0} tone="amber" icon={AlertTriangle}/>
      </KpiRow>
      <PayFilters q={q} setQ={setQ} onSearch={load} placeholder="Bank / brand / last4 / label"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="pm-type" value={type} onChange={(e) => setType(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All types</option>
            <option value="card">Card</option>
            <option value="bank">Bank</option>
          </select>
        }
        counts={data?.by_type} total={data?.total}/>
      <PayTable loading={loading} rows={data?.rows || []} testid="pm" empty="No payment methods in this window."
        columns={[
          { l: "Owner",  r: (x) => <PayStack top={x.owner_company || x.owner_email} sub={`${x.owner_email || "—"} · ${x.owner_role || "—"}`}/> },
          { l: "Type",   r: (x) => <span className="text-[11px] uppercase mono">{x.type || "—"}</span> },
          { l: "Label",  r: (x) => <span className="text-[12.5px]">{x.label || "—"}</span> },
          { l: "Last4",  r: (x) => <span className="mono text-[11px]">···· {x.last4 || "?"}</span> },
          { l: "Rails",  r: (x) => <span className="text-[10.5px] text-foreground/55">{x.rails || "—"}</span> },
          { l: "Default", r: (x) => x.is_default ? <span className="chip border bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]"><Star className="w-2.5 h-2.5 inline"/> Default</span> : <span className="text-[11px] text-foreground/40">—</span> },
          { l: "Verified", r: (x) => x.verified ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> : <span className="text-[11px] text-foreground/40">—</span> },
          { l: "Added", r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.created_at)}</span> },
        ]}/>
    </div>
  );
}


function TopupsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [minAmt, setMinAmt] = useState(0);
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays), min_amount: String(minAmt) });
      if (status) p.set("status", status);
      const r = await api.get(`/admin/wallet/topups?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, minAmt, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-topups">
      <KpiRow>
        <KpiSm l="Topups" v={data?.kpis?.total_count ?? 0} icon={ArrowDownRight} tone="emerald"/>
        <KpiSm l="Total $" v={money(data?.kpis?.total_volume_usd ?? 0)} tone="emerald"/>
        <KpiSm l="Avg ticket" v={money(data?.kpis?.avg_ticket_usd ?? 0)}/>
        <KpiSm l="Max ticket" v={money(data?.kpis?.max_ticket_usd ?? 0)} tone="amber"/>
      </KpiRow>
      <PayFilters q="" setQ={() => {}} hideSearch onSearch={load} sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="tu-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <select data-testid="tu-min" value={String(minAmt)} onChange={(e) => setMinAmt(Number(e.target.value))}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="0">Any amount</option>
              <option value="1000">≥ $1K</option>
              <option value="5000">≥ $5K</option>
              <option value="10000">≥ $10K</option>
              <option value="50000">≥ $50K</option>
            </select>
          </>
        }
        counts={data?.by_status} total={data?.total}/>

      {data?.top_users?.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="tu-top-users">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">Top top-up users in window</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {data.top_users.slice(0, 5).map((u) => (
              <div key={u.user_id} className="rounded-lg border border-border p-2 text-[11.5px]">
                <div className="font-medium truncate">{u.owner_company || u.owner_email || u.user_id?.slice(0, 8)}</div>
                <div className="text-foreground/55 text-[10.5px]">{u.count}× · {money(u.sum_usd)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <PayTable loading={loading} rows={data?.rows || []} testid="tu" empty="No topups in this window."
        columns={[
          { l: "Owner",  r: (x) => <PayStack top={x.owner_company || x.owner_email} sub={x.owner_email}/> },
          { l: "Amount", r: (x) => <span className="display tabular-nums text-emerald-700">{money(x.amount)}</span> },
          { l: "Method", r: (x) => <span className="text-[11px] text-foreground/55">{x.method || "—"}</span> },
          { l: "Rail",   r: (x) => <span className="text-[10.5px] mono uppercase text-foreground/55">{x.rail || "—"}</span> },
          { l: "Status", r: (x) => <StatusPill v={x.status}/> },
          { l: "Ref",    r: (x) => <span className="mono text-[10.5px] text-foreground/55 max-w-[140px] truncate inline-block">{x.ref || "—"}</span> },
          { l: "At",     r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.created_at)}</span> },
        ]}/>
    </div>
  );
}


function WithdrawalsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [minAmt, setMinAmt] = useState(0);
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays), min_amount: String(minAmt) });
      if (status) p.set("status", status);
      const r = await api.get(`/admin/wallet/withdrawals?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, minAmt, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-withdrawals">
      <KpiRow>
        <KpiSm l="Withdrawals" v={data?.kpis?.total_count ?? 0} icon={ArrowUpRight} tone="rose"/>
        <KpiSm l="Total $" v={money(data?.kpis?.total_volume_usd ?? 0)} tone="rose"/>
        <KpiSm l="Avg ticket" v={money(data?.kpis?.avg_ticket_usd ?? 0)}/>
        <KpiSm l="Max ticket" v={money(data?.kpis?.max_ticket_usd ?? 0)} tone="amber"/>
      </KpiRow>
      <PayFilters q="" setQ={() => {}} hideSearch onSearch={load} sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="wd-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <select data-testid="wd-min" value={String(minAmt)} onChange={(e) => setMinAmt(Number(e.target.value))}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="0">Any amount</option>
              <option value="500">≥ $500</option>
              <option value="2000">≥ $2K</option>
              <option value="10000">≥ $10K</option>
            </select>
          </>
        }
        counts={data?.by_status} total={data?.total}/>

      {data?.top_users?.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="wd-top-users">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">Top withdrawing users in window</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {data.top_users.slice(0, 5).map((u) => (
              <div key={u.user_id} className="rounded-lg border border-border p-2 text-[11.5px]">
                <div className="font-medium truncate">{u.owner_company || u.owner_email || u.user_id?.slice(0, 8)}</div>
                <div className="text-foreground/55 text-[10.5px]">{u.count}× · {money(u.sum_usd)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <PayTable loading={loading} rows={data?.rows || []} testid="wd" empty="No withdrawals in this window."
        columns={[
          { l: "Owner",  r: (x) => <PayStack top={x.owner_company || x.owner_email} sub={x.owner_email}/> },
          { l: "Amount", r: (x) => <span className="display tabular-nums text-rose-700">{money(x.amount_usd)}</span> },
          { l: "Method", r: (x) => <span className="text-[11px] text-foreground/55">{x.method || "—"}</span> },
          { l: "Rail",   r: (x) => <span className="text-[10.5px] mono uppercase text-foreground/55">{x.rail || "—"}</span> },
          { l: "Status", r: (x) => <StatusPill v={x.status}/> },
          { l: "Ref",    r: (x) => <span className="mono text-[10.5px] text-foreground/55 max-w-[140px] truncate inline-block">{x.ref || "—"}</span> },
          { l: "At",     r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.created_at)}</span> },
        ]}/>
    </div>
  );
}


function StatementsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userIdFilter, setUserIdFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "200" });
      if (userIdFilter.trim()) p.set("user_id", userIdFilter.trim());
      const r = await api.get(`/admin/wallet/statements?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dl = async (uid, y, m, ext) => {
    try {
      const r = await api.get(`/wallet/statements/${y}/${m}.${ext}?user_id=${uid}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `statement-${uid.slice(0, 8)}-${y}-${String(m).padStart(2, "0")}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  return (
    <div className="space-y-3" data-testid="adm-statements">
      <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
        <Input data-testid="stmt-uid" value={userIdFilter} onChange={(e) => setUserIdFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder="Filter by user_id (optional)" className="h-9 text-[12.5px] w-[280px]"/>
        <button data-testid="stmt-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
        <div className="ml-auto text-[11px] text-foreground/55">{data?.total ?? 0} month-rows</div>
      </div>
      <PayTable loading={loading} rows={data?.months || []} testid="stmt" empty="No statements found."
        columns={[
          { l: "Owner", r: (x) => <PayStack top={x.owner_company || x.owner_email} sub={`${x.owner_email || "—"} · ${x.owner_role || "—"}`}/> },
          { l: "Period", r: (x) => <span className="text-[12.5px]">{x.label}</span> },
          { l: "Txns",  r: (x) => <span className="text-[11px]">{x.tx_count}</span> },
          { l: "In",    r: (x) => <span className="display tabular-nums text-emerald-700">{money(x.in)}</span> },
          { l: "Out",   r: (x) => <span className="display tabular-nums text-rose-700">{money(x.out)}</span> },
          { l: "Net",   r: (x) => <span className="display tabular-nums">{money(x.net)}</span> },
          { l: "Download", r: (x) => (
            <div className="flex items-center gap-1">
              <button onClick={() => dl(x.user_id, x.year, x.month, "pdf")}
                data-testid={`stmt-pdf-${x.user_id?.slice(0, 6)}-${x.year}-${x.month}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[10.5px] hover:bg-secondary">
                <FileText size={11}/>PDF
              </button>
              <button onClick={() => dl(x.user_id, x.year, x.month, "csv")}
                data-testid={`stmt-csv-${x.user_id?.slice(0, 6)}-${x.year}-${x.month}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[10.5px] hover:bg-secondary">
                <Download size={11}/>CSV
              </button>
            </div>
          )},
        ]}/>
    </div>
  );
}


function LifecycleView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (kind) p.set("event_kind", kind);
      const r = await api.get(`/admin/payment-methods/lifecycle-log?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-lifecycle">
      <PayFilters q="" setQ={() => {}} hideSearch onSearch={load} sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="lc-kind" value={kind} onChange={(e) => setKind(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All events</option>
            <option value="added">Added</option>
            <option value="deleted">Deleted</option>
            <option value="set_default">Set default</option>
          </select>
        }
        counts={data?.by_kind} total={data?.total}/>
      <PayTable loading={loading} rows={data?.rows || []} testid="lc" empty="No lifecycle events in this window."
        columns={[
          { l: "Owner", r: (x) => <PayStack top={x.owner_company || x.owner_email} sub={`${x.owner_email || "—"} · ${x.owner_role || "—"}`}/> },
          { l: "Event", r: (x) => <KindPill k={x.kind}/> },
          { l: "Type",  r: (x) => <span className="text-[11px] uppercase mono">{x.type || "—"}</span> },
          { l: "Label", r: (x) => <span className="text-[12.5px]">{x.label || x.bank_name || "—"}</span> },
          { l: "Last4", r: (x) => <span className="mono text-[11px]">···· {x.last4 || "?"}</span> },
          { l: "Actor", r: (x) => <span className="text-[10.5px] mono uppercase text-foreground/55">{x.actor_role || "—"}</span> },
          { l: "At",    r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.at)}</span> },
        ]}/>
    </div>
  );
}


/* ─── Lender visibility views (iter-93) ──────────────────────────────── */
function LenderWalletsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [minBal, setMinBal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set("q", q.trim());
      if (minBal > 0) p.set("min_balance_usd", String(minBal));
      const r = await api.get(`/admin/lender-wallets?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [minBal]);

  return (
    <div className="space-y-3" data-testid="adm-lender-wallets">
      <KpiRow>
        <KpiSm l="Lenders" v={data?.total ?? 0} icon={Wallet}/>
        <KpiSm l="Total balance" v={money(data?.kpis?.total_balance_usd ?? 0)} tone="emerald"/>
        <KpiSm l="Topups · 30d" v={money(data?.kpis?.topup_30d_total_usd ?? 0)} tone="emerald" icon={ArrowDownRight}/>
        <KpiSm l="Outflows · 30d" v={money(data?.kpis?.outflow_30d_total_usd ?? 0)} tone="rose" icon={ArrowUpRight}/>
      </KpiRow>
      <PayFilters q={q} setQ={setQ} onSearch={load} placeholder="Lender org name…"
        sinceDays={30} setSinceDays={() => {}}
        extra={
          <select data-testid="lw-min-bal" value={String(minBal)} onChange={(e) => setMinBal(Number(e.target.value))}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="0">Any balance</option>
            <option value="100">≥ $100</option>
            <option value="10000">≥ $10K</option>
            <option value="100000">≥ $100K</option>
            <option value="1000000">≥ $1M</option>
          </select>
        }
        total={data?.total}/>
      <PayTable loading={loading} rows={data?.rows || []} testid="lw" empty="No lender wallets configured."
        columns={[
          { l: "Lender", r: (x) => <PayStack top={x.org_name} sub={`${x.org_status || "—"} · ${x.wallet_user_id}`}/> },
          { l: "Balance", r: (x) => <span className="display text-[14px] tabular-nums">{money(x.balance_usd)}</span> },
          { l: "Outstanding", r: (x) => <PayStack top={money(x.outstanding_principal_usd)} sub={`${x.outstanding_loans} loans`}/> },
          { l: "30d inflow",  r: (x) => <PayStack top={money(x.inflow_30d_usd)} sub={`${x.inflow_30d_count} txns`}/> },
          { l: "30d outflow", r: (x) => <PayStack top={money(x.outflow_30d_usd)} sub={`${x.outflow_30d_count} txns`}/> },
          { l: "Allianz policy", r: (x) => x.allianz_policy_id
              ? <PayStack top={x.allianz_policy_id} sub={`${x.allianz_policy_status || "—"} · ${money(x.allianz_total_limit_usd)}`}/>
              : <span className="text-[11px] text-foreground/40">— not configured</span> },
          { l: "Last activity", r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.last_activity_at)}</span> },
        ]}/>
    </div>
  );
}


function LenderTxnsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txnType, setTxnType] = useState("");
  const [direction, setDirection] = useState("");
  const [minAmt, setMinAmt] = useState(0);
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (txnType)   p.set("txn_type", txnType);
      if (direction) p.set("direction", direction);
      if (minAmt > 0) p.set("min_amount_usd", String(minAmt));
      const r = await api.get(`/admin/lender-wallets/transactions?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [txnType, direction, minAmt, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-lender-txns">
      <KpiRow>
        <KpiSm l="Txns" v={data?.kpis?.total_count ?? 0} icon={ScrollText}/>
        <KpiSm l="Volume" v={money(data?.kpis?.total_volume_usd ?? 0)}/>
        <KpiSm l="Window" v={`${sinceDays}d`}/>
      </KpiRow>
      <PayFilters q="" setQ={() => {}} hideSearch onSearch={load}
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="lt-type" value={txnType} onChange={(e) => setTxnType(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All types</option>
              <option value="lender_topup">Lender topup</option>
              <option value="lender_withdraw">Lender withdraw</option>
              <option value="loan_disbursement">Loan disbursement</option>
              <option value="loan_repayment">Loan repayment</option>
              <option value="loan_repayment_partial">Partial repayment</option>
              <option value="loan_markup">Markup share</option>
              <option value="allianz_takeover_payout">Allianz takeover payout</option>
            </select>
            <select data-testid="lt-dir" value={direction} onChange={(e) => setDirection(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All directions</option>
              <option value="in">In</option>
              <option value="out">Out</option>
            </select>
            <select data-testid="lt-min" value={String(minAmt)} onChange={(e) => setMinAmt(Number(e.target.value))}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="0">Any amount</option>
              <option value="1000">≥ $1K</option>
              <option value="10000">≥ $10K</option>
              <option value="100000">≥ $100K</option>
            </select>
          </>
        }
        counts={data?.by_type ? Object.fromEntries(Object.entries(data.by_type).map(([k, v]) => [k, v.n])) : null}
        total={data?.total}/>
      <PayTable loading={loading} rows={data?.rows || []} testid="lt" empty="No lender transactions in this window."
        columns={[
          { l: "Lender", r: (x) => <PayStack top={x.org_name || "—"} sub={x.org_id?.slice(0, 8)}/> },
          { l: "Type",   r: (x) => <span className="text-[11px] mono">{x.type}</span> },
          { l: "Amount", r: (x) => <span className={`display text-[14px] tabular-nums ${x.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {x.amount >= 0 ? "+" : ""}{money(x.amount)}</span> },
          { l: "Direction", r: (x) => <span className="text-[10.5px] uppercase mono text-foreground/55">{x.direction || "—"}</span> },
          { l: "Status", r: (x) => <StatusPill v={x.status}/> },
          { l: "Loan #", r: (x) => x.metadata?.loan_number ? <span className="mono text-[10.5px]">{x.metadata.loan_number}</span> : <span className="text-[11px] text-foreground/40">—</span> },
          { l: "Ref",    r: (x) => <span className="text-[10.5px] mono text-foreground/55">{x.ref || "—"}</span> },
          { l: "At",     r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.created_at)}</span> },
        ]}/>
    </div>
  );
}


function LenderPoliciesView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [configuredOnly, setConfiguredOnly] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (status) p.set("status", status);
      if (configuredOnly) p.set("configured_only", "true");
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/lender-policies?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, configuredOnly]);

  return (
    <div className="space-y-3" data-testid="adm-lender-policies">
      <KpiRow>
        <KpiSm l="Lenders" v={data?.kpis?.total_lenders ?? 0}/>
        <KpiSm l="Configured" v={data?.kpis?.configured ?? 0} tone="emerald" icon={CheckCircle2}/>
        <KpiSm l="Active limit" v={money(data?.kpis?.total_limit_usd ?? 0)} tone="emerald"/>
        <KpiSm l="Utilization" v={`${data?.kpis?.utilization_pct ?? 0}%`} tone="amber"/>
      </KpiRow>
      <PayFilters q={q} setQ={setQ} onSearch={load} placeholder="Lender org name…"
        sinceDays={30} setSinceDays={() => {}}
        extra={
          <>
            <select data-testid="lp-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All policies</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
            </select>
            <label className="text-[12.5px] flex items-center gap-1.5 px-2 cursor-pointer">
              <input data-testid="lp-configured-only" type="checkbox" checked={configuredOnly}
                onChange={(e) => setConfiguredOnly(e.target.checked)}/> Configured only
            </label>
          </>
        }
        total={data?.total}/>
      <PayTable loading={loading} rows={data?.rows || []} testid="lp" empty="No lender policies."
        columns={[
          { l: "Lender", r: (x) => <PayStack top={x.org_name} sub={x.org_status || "—"}/> },
          { l: "Policy ID", r: (x) => x.policy_id ? <span className="mono text-[11px]">{x.policy_id}</span> : <span className="text-[11px] text-foreground/40">— not issued</span> },
          { l: "BU", r: (x) => <span className="text-[10.5px] uppercase mono text-foreground/55">{x.business_unit || "—"}</span> },
          { l: "Status", r: (x) => <StatusPill v={x.policy_status || (x.configured ? "—" : "unconfigured")}/> },
          { l: "Limit", r: (x) => <span className="display text-[14px] tabular-nums">{money(x.total_limit_usd)}</span> },
          { l: "Used", r: (x) => <PayStack top={money(x.used_limit_usd)}
              sub={x.total_limit_usd > 0 ? `${((x.used_limit_usd / x.total_limit_usd) * 100).toFixed(1)}%` : "—"}/> },
          { l: "Available", r: (x) => <span className="tabular-nums text-emerald-700">{money(x.available_limit_usd)}</span> },
          { l: "Activated", r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.activated_at)}</span> },
        ]}/>
    </div>
  );
}


function GrantProposalsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [grade, setGrade] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (grade)  p.set("allianz_grade", grade);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/credit-grant-proposals?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, grade, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-grant-proposals">
      <KpiRow>
        <KpiSm l="Proposals" v={data?.kpis?.total_count ?? 0}/>
        <KpiSm l="Pending" v={data?.kpis?.pending_count ?? 0} tone="amber"/>
        <KpiSm l="Approved" v={data?.kpis?.approved_count ?? 0} tone="emerald" icon={CheckCircle2}/>
        <KpiSm l="Rejected" v={data?.kpis?.rejected_count ?? 0} tone="rose" icon={XCircle}/>
      </KpiRow>
      <PayFilters q={q} setQ={setQ} onSearch={load} placeholder="Buyer email / company / name…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="gp-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="counter">Counter</option>
              <option value="rejected_by_insurance">Rejected by insurance</option>
            </select>
            <select data-testid="gp-grade" value={grade} onChange={(e) => setGrade(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All grades</option>
              <option value="A">A · Excellent</option>
              <option value="B">B · Strong</option>
              <option value="C">C · Acceptable</option>
              <option value="D">D · Watch</option>
              <option value="E">E · Concerning</option>
              <option value="F">F · High risk</option>
            </select>
          </>
        }
        counts={data?.by_status ? Object.fromEntries(Object.entries(data.by_status).map(([k, v]) => [k, v.n])) : null}
        total={data?.total}/>
      <PayTable loading={loading} rows={data?.rows || []} testid="gp" empty="No proposals in this window."
        columns={[
          { l: "Buyer", r: (x) => <PayStack top={x.buyer_company_hydrated || x.buyer_company || "—"} sub={x.buyer_email || "—"}/> },
          { l: "Requested", r: (x) => <span className="display text-[14px] tabular-nums">{money(x.requested_limit_usd)}</span> },
          { l: "Terms", r: (x) => <span className="text-[11px] uppercase mono">{x.default_terms || "—"}</span> },
          { l: "Lender", r: (x) => <PayStack top={x.target_lender_name || "—"} sub={x.target_lender_org_id?.slice(0, 8) || "Any active"}/> },
          { l: "Allianz", r: (x) => x.allianz_grade
              ? <span className={`chip border text-[10.5px] ${["A","B","C"].includes(x.allianz_grade) ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                  {x.allianz_grade} · {x.allianz_outlook || "—"}</span>
              : <span className="text-[11px] text-foreground/40">—</span> },
          { l: "Status", r: (x) => <StatusPill v={x.status}/> },
          { l: "Pending", r: (x) => x.status === "pending" && x.days_pending != null
              ? <span className={`text-[11px] mono ${x.days_pending > 3 ? "text-rose-700 font-medium" : "text-foreground/55"}`}>{x.days_pending}d</span>
              : <span className="text-[11px] text-foreground/40">—</span> },
          { l: "Created", r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.created_at)}</span> },
        ]}/>
    </div>
  );
}


/* ─── Loans Roster (iter-94 · P2 admin gap) ──────────────────────────── */
const FUNDER_TONE = {
  lender: "bg-blue-50 text-blue-700 border-blue-200",
  seller: "bg-violet-50 text-violet-700 border-violet-200",
};
const AGING_TONE = {
  current:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  "1-30":         "bg-amber-50 text-amber-800 border-amber-200",
  "31-60":        "bg-orange-50 text-orange-800 border-orange-200",
  "60+":          "bg-rose-50 text-rose-700 border-rose-200",
  allianz_taken:  "bg-slate-100 text-slate-700 border-slate-200",
};
const LOAN_STATUS_TONE = {
  active:               "bg-emerald-50 text-emerald-700 border-emerald-200",
  past_due:             "bg-amber-50 text-amber-800 border-amber-200",
  past_due_tier2:       "bg-orange-50 text-orange-800 border-orange-200",
  past_due_tier3:       "bg-rose-50 text-rose-700 border-rose-200",
  insurance_takeover:   "bg-slate-100 text-slate-700 border-slate-200",
  paid:                 "bg-emerald-50 text-emerald-700 border-emerald-200",
  void:                 "bg-slate-100 text-slate-700 border-slate-200",
  nsf_pending:          "bg-amber-50 text-amber-800 border-amber-200",
  extended:             "bg-blue-50 text-blue-700 border-blue-200",
};
function FunderPill({ v }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${FUNDER_TONE[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}
      data-testid={`loan-funder-pill-${v || "unknown"}`}>
      {v || "lender"}
    </span>
  );
}
function AgingPill({ v }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${AGING_TONE[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {v === "allianz_taken" ? "Allianz" : v || "—"}
    </span>
  );
}
function LoanStatusPill({ v }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${LOAN_STATUS_TONE[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {(v || "—").replace(/_/g, " ")}
    </span>
  );
}

function LoansRosterView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [funderType, setFunderType] = useState("");      // "", "lender", "seller"
  const [aging, setAging]           = useState("");      // "", "current", "1-30", ...
  const [status, setStatus]         = useState("");
  const [q, setQ]                   = useState("");
  const [sinceDays, setSinceDays]   = useState(365);
  const [exporting, setExporting]   = useState(false);

  const buildParams = () => {
    const p = new URLSearchParams({ since_days: String(sinceDays), limit: "200" });
    if (funderType) p.set("funder_type", funderType);
    if (aging)      p.set("aging_bucket", aging);
    if (status)     p.set("status", status);
    if (q.trim())   p.set("q", q.trim());
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/loans?${buildParams().toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load loans");
    } finally { setLoading(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (funderType) p.set("funder_type", funderType);
      if (aging)      p.set("aging_bucket", aging);
      if (status)     p.set("status", status);
      if (q.trim())   p.set("q", q.trim());
      const r = await api.get(`/admin/loans/export.csv?${p.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `admin_loans_${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported loans roster");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [funderType, aging, status, sinceDays]);

  const aging_counts = data?.by_aging_bucket || {};
  const funder_counts = data?.by_funder_type || {};

  return (
    <div className="space-y-3" data-testid="adm-loans-roster">
      <KpiRow>
        <KpiSm l="Total loans" v={data?.kpis?.total_count ?? 0}/>
        <KpiSm l="Outstanding principal" v={money(data?.kpis?.outstanding_principal_usd ?? 0)} tone="amber"/>
        <KpiSm l="Outstanding fees" v={money(data?.kpis?.outstanding_fees_usd ?? 0)} tone="amber"/>
        <KpiSm l="Allianz-taken" v={aging_counts.allianz_taken ?? 0} tone="rose"/>
      </KpiRow>

      {/* Funder-type + aging-bucket pivot chips */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mr-1 self-center">Funder:</span>
          {[
            { v: "",       l: "All" },
            { v: "lender", l: `Lender (${funder_counts.lender ?? 0})` },
            { v: "seller", l: `Seller (${funder_counts.seller ?? 0})` },
          ].map(c => (
            <button key={c.v} onClick={() => setFunderType(c.v)}
              data-testid={`loans-funder-${c.v || "all"}`}
              className={`text-[11.5px] rounded-full px-3 py-1 border transition-all ${
                funderType === c.v ? "bg-foreground text-background border-foreground"
                                   : "bg-white border-border text-foreground/65 hover:border-foreground/40"
              }`}>{c.l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 ml-3">
          <span className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mr-1 self-center">Aging:</span>
          {[
            { v: "",              l: "All" },
            { v: "current",       l: `Current (${aging_counts.current ?? 0})` },
            { v: "1-30",          l: `1-30d (${aging_counts["1-30"] ?? 0})` },
            { v: "31-60",         l: `31-60d (${aging_counts["31-60"] ?? 0})` },
            { v: "60+",           l: `60+d (${aging_counts["60+"] ?? 0})` },
            { v: "allianz_taken", l: `Allianz (${aging_counts.allianz_taken ?? 0})` },
          ].map(c => (
            <button key={c.v} onClick={() => setAging(c.v)}
              data-testid={`loans-aging-${c.v || "all"}`}
              className={`text-[11.5px] rounded-full px-3 py-1 border transition-all ${
                aging === c.v ? "bg-foreground text-background border-foreground"
                              : "bg-white border-border text-foreground/65 hover:border-foreground/40"
              }`}>{c.l}</button>
          ))}
        </div>
      </div>

      <PayFilters q={q} setQ={setQ} onSearch={load} placeholder="loan_number / invoice / order…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="loans-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="past_due">Past due</option>
              <option value="past_due_tier2">Past due · tier 2</option>
              <option value="past_due_tier3">Past due · tier 3</option>
              <option value="paid">Paid</option>
              <option value="insurance_takeover">Allianz takeover</option>
              <option value="void">Void</option>
              <option value="nsf_pending">NSF pending</option>
              <option value="extended">Extended</option>
            </select>
            <button onClick={exportCsv} disabled={exporting}
              data-testid="loans-export-csv"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary disabled:opacity-50">
              {exporting ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>} Export CSV
            </button>
          </>
        }
        counts={data?.by_status}
        total={data?.total}/>

      <PayTable loading={loading} rows={data?.rows || []} testid="loans" empty="No loans match these filters."
        columns={[
          { l: "Loan #",  r: (x) => <span className="mono text-[11px]" data-testid={`loan-num-${x.id || ""}`}>{x.loan_number || "—"}</span> },
          { l: "Funder",  r: (x) => <FunderPill v={x.funder_type}/> },
          { l: "Status",  r: (x) => <LoanStatusPill v={x.status}/> },
          { l: "Aging",   r: (x) => <AgingPill v={x.aging_bucket}/> },
          { l: "Terms",   r: (x) => <span className="text-[11px] uppercase mono">{x.terms || "—"}</span> },
          { l: "Principal", r: (x) => <span className="display text-[13.5px] tabular-nums">{money(x.principal)}</span> },
          { l: "Outstanding", r: (x) => <PayStack top={money(x.principal_remaining)} sub={`fee ${money(x.fee_owed)}`}/> },
          { l: "DPD",     r: (x) => <span className={`mono text-[11px] ${x.days_past_due > 0 ? "text-rose-700 font-medium" : "text-foreground/55"}`}>{x.days_past_due ?? 0}</span> },
          { l: "Due",     r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.due_date)}</span> },
          { l: "Opened",  r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.opened_at)}</span> },
        ]}/>
    </div>
  );
}

/* ─── iter-96 · Buyer-side P1 payment visibility ──────────────────────── */
const CARD_STATUS_TONE = {
  active:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  frozen:    "bg-blue-50 text-blue-700 border-blue-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};
function CardStatusPill({ v }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${CARD_STATUS_TONE[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {v || "—"}
    </span>
  );
}

function CardsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [sellerLocked, setSellerLocked] = useState(false);
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(365);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (sellerLocked) p.set("seller_locked_only", "true");
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/cards/list?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sellerLocked, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-cards">
      <KpiRow>
        <KpiSm l="Total cards"  v={data?.kpis?.total_count ?? 0} icon={CreditCard}/>
        <KpiSm l="Active"       v={data?.kpis?.active_count ?? 0} tone="emerald" icon={CheckCircle2}/>
        <KpiSm l="Total limit"  v={money(data?.kpis?.total_limit_usd ?? 0)}/>
        <KpiSm l="Utilization"  v={`${data?.kpis?.utilization_pct ?? 0}%`} tone="amber"/>
      </KpiRow>

      <PayFilters q={q} setQ={setQ} onSearch={load} placeholder="label / last4 / seller lock…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="cards-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="frozen">Frozen</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <label className="text-[12.5px] flex items-center gap-1.5 px-2 cursor-pointer">
              <input data-testid="cards-seller-locked" type="checkbox" checked={sellerLocked}
                onChange={(e) => setSellerLocked(e.target.checked)}/> Seller-locked only
            </label>
          </>
        }
        counts={data?.by_status}
        total={data?.total}/>

      {data?.top_spenders && data.top_spenders.length > 0 && (
        <div className="rounded-2xl bg-white border border-border p-3" data-testid="cards-top-spenders">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2">
            Top spenders <span className="text-amber-700">· spend management</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_spenders.map((t, i) => (
              <div key={t.user_id} data-testid={`cards-top-${i}`}
                className="bg-secondary/40 rounded-lg p-2 text-[11px]">
                <div className="font-medium truncate">{t.user_company || t.user_email || "—"}</div>
                <div className="text-foreground/55 mono">{t.card_count} cards · {money(t.total_spent_usd)} / {money(t.total_limit_usd)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <PayTable loading={loading} rows={data?.rows || []} testid="cards" empty="No cards match these filters."
        columns={[
          { l: "User",     r: (x) => <PayStack top={x.user_company || x.user_email || "—"} sub={`••${x.last4 || "—"}`}/> },
          { l: "Label",    r: (x) => <span className="text-[11.5px]">{x.label || "—"}</span> },
          { l: "Seller lock", r: (x) => x.seller_lock
              ? <span className="chip border bg-amber-50 text-amber-800 border-amber-200 text-[10.5px]">{x.seller_lock}</span>
              : <span className="text-foreground/40 text-[11px]">unlocked</span> },
          { l: "Limit",    r: (x) => <span className="display tabular-nums">{money(x.limit)}</span> },
          { l: "Spent",    r: (x) => <PayStack top={money(x.spent)}
              sub={`${x.utilization_pct ?? 0}% · ${money(x.available_usd)} avail`}/> },
          { l: "Status",   r: (x) => <CardStatusPill v={x.status}/> },
          { l: "Expiry",   r: (x) => <span className="text-[11px] mono text-foreground/55">{x.expiry || "—"}</span> },
          { l: "Issued",   r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.created_at)}</span> },
        ]}/>
    </div>
  );
}


function MultiPayBatchesView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("");
  const [q, setQ] = useState("");
  const [minTotal, setMinTotal] = useState("");
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (method) p.set("method_type", method);
      if (q.trim()) p.set("q", q.trim());
      if (minTotal) p.set("min_total", minTotal);
      const r = await api.get(`/admin/payments/multi-pay-batches?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [method, minTotal, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-multi-pay">
      <KpiRow>
        <KpiSm l="Batches"           v={data?.kpis?.total_batches ?? 0} icon={History}/>
        <KpiSm l="Total paid"        v={money(data?.kpis?.total_paid_usd ?? 0)} tone="emerald"/>
        <KpiSm l="Processing fees"   v={money(data?.kpis?.total_fees_usd ?? 0)} tone="amber"/>
        <KpiSm l="Avg invoices/batch" v={data?.kpis?.avg_invoices_per_batch ?? 0}/>
      </KpiRow>

      <PayFilters q={q} setQ={setQ} onSearch={load} placeholder="TXN-TQ-… transaction code"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="multi-pay-method" value={method} onChange={(e) => setMethod(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All methods</option>
              <option value="card">Card</option>
              <option value="ach">ACH</option>
              <option value="wire">Wire</option>
            </select>
            <select data-testid="multi-pay-min" value={minTotal} onChange={(e) => setMinTotal(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">Any amount</option>
              <option value="1000">≥ $1,000</option>
              <option value="10000">≥ $10,000</option>
              <option value="50000">≥ $50,000</option>
            </select>
          </>
        }
        total={data?.total}/>

      <PayTable loading={loading} rows={data?.rows || []} testid="multi-pay" empty="No multi-pay batches in this window."
        columns={[
          { l: "Transaction", r: (x) => <span className="mono text-[11.5px] font-medium">{x.transaction}</span> },
          { l: "Buyer",       r: (x) => <PayStack top={x.buyer_company || "—"} sub={x.buyer_email}/> },
          { l: "Method",      r: (x) => <span className="text-[11px] uppercase mono text-foreground/65">{x.method_type || "—"}</span> },
          { l: "Invoices",    r: (x) => <span className="display tabular-nums">{x.invoice_count ?? 0}</span> },
          { l: "Total paid",  r: (x) => <span className="display text-[13.5px] tabular-nums text-emerald-700">{money(x.total_paid)}</span> },
          { l: "Fee",         r: (x) => x.processing_fee
              ? <span className="text-amber-700 mono text-[11px]">{money(x.processing_fee)}</span>
              : <span className="text-foreground/40 text-[11px]">—</span> },
          { l: "Paid",        r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.created_at)}</span> },
        ]}/>
    </div>
  );
}


const CA_STATUS_TONE = {
  pending:  "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-rose-50 text-rose-700 border-rose-200",
};
function CAStatusPill({ v }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${CA_STATUS_TONE[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {v || "—"}
    </span>
  );
}

function CreditApplicationsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [terms, setTerms] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (terms) p.set("terms", terms);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/credit-applications/list?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, terms, sinceDays]);

  return (
    <div className="space-y-3" data-testid="adm-credit-apps">
      <KpiRow>
        <KpiSm l="Applications"   v={data?.kpis?.total_count ?? 0} icon={FileText}/>
        <KpiSm l="Approval rate"  v={`${data?.kpis?.approval_rate_pct ?? 0}%`} tone="emerald" icon={CheckCircle2}/>
        <KpiSm l="Total requested" v={money(data?.kpis?.total_amount_requested ?? 0)}/>
        <KpiSm l="Approved $"     v={money(data?.kpis?.approved_amount_usd ?? 0)} tone="emerald"/>
      </KpiRow>

      <PayFilters q={q} setQ={setQ} onSearch={load} placeholder="buyer name / company…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="ca-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
            </select>
            <select data-testid="ca-terms" value={terms} onChange={(e) => setTerms(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All terms</option>
              <option value="net30">Net-30</option>
              <option value="net60">Net-60</option>
              <option value="net90">Net-90</option>
            </select>
          </>
        }
        counts={data?.by_status}
        total={data?.total}/>

      {data?.top_buyers && data.top_buyers.length > 0 && (
        <div className="rounded-2xl bg-white border border-border p-3" data-testid="ca-top-buyers">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2">
            Top buyers by total requested <span className="text-emerald-700">· demand signal</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_buyers.map((t, i) => (
              <div key={t.buyer_id} data-testid={`ca-top-${i}`}
                className="bg-secondary/40 rounded-lg p-2 text-[11px]">
                <div className="font-medium truncate">{t.buyer_company || t.buyer_email || "—"}</div>
                <div className="text-foreground/55 mono">{t.applications} apps · {money(t.total_requested_usd)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <PayTable loading={loading} rows={data?.rows || []} testid="ca" empty="No credit applications in this window."
        columns={[
          { l: "Buyer",     r: (x) => <PayStack top={x.buyer_company || x.buyer_name || "—"} sub={x.buyer_email}/> },
          { l: "Amount",    r: (x) => <span className="display text-[14px] tabular-nums">{money(x.amount)}</span> },
          { l: "Terms",     r: (x) => <span className="text-[11px] uppercase mono">{x.terms || "—"}</span> },
          { l: "Status",    r: (x) => <CAStatusPill v={x.status}/> },
          { l: "Rate",      r: (x) => <span className="mono text-[11px] text-foreground/65">{((x.rate || 0) * 100).toFixed(2)}%</span> },
          { l: "Submitted", r: (x) => <span className="text-[11px] text-foreground/55">{fmtDate(x.created_at)}</span> },
        ]}/>
    </div>
  );
}



/* ─── shared mini-kit (iter-85) ──────────────────────────────────────── */
function KpiRow({ children }) {
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>;
}

function KpiSm({ l, v, tone, icon: Icon }) {
  const toneCls = { emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700" }[tone] || "text-foreground/85";
  return (
    <div className="rounded-2xl bg-white border border-border p-4">
      <div className="flex items-center gap-2 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
        {Icon && <Icon className="w-3 h-3"/>}{l}
      </div>
      <div className={`display text-2xl mt-1 ${toneCls}`}>{v}</div>
    </div>
  );
}

function PayFilters({ q, setQ, onSearch, placeholder, sinceDays, setSinceDays, extra, counts, total, hideSearch }) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
      {!hideSearch && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
          <Input data-testid="pay-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
            placeholder={placeholder} className="h-9 pl-8 text-[13px] w-[260px]"/>
        </div>
      )}
      {extra}
      <select data-testid="pay-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
        className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="180">Last 6 months</option>
        <option value="365">Last 1 year</option>
        <option value="730">Last 2 years</option>
      </select>
      <button data-testid="pay-refresh" onClick={onSearch}
        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
        <RefreshCw size={13}/> Refresh
      </button>
      <div className="ml-auto flex items-center gap-3 text-[11px] text-foreground/55">
        <span>{total ?? 0} rows</span>
        {counts && Object.keys(counts).length > 0 && (
          <span className="flex items-center gap-1.5">
            {Object.entries(counts).slice(0, 4).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-secondary/60 border border-border">
                <span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium">{v}</span>
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

function PayTable({ loading, rows, columns, empty, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid={`${testid}-table`}>
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
          <tr>{columns.map((c, i) => <th key={i} className="px-4 py-2.5 font-medium">{c.l}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55" data-testid={`${testid}-empty`}>{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id || r.method_id || `${r.user_id}-${r.year}-${r.month}` || i} data-testid={`${testid}-row-${i}`} className="hover:bg-secondary/20">
              {columns.map((c, j) => <td key={j} className="px-4 py-3">{c.r(r, i)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayStack({ top, sub }) {
  return (
    <div className="leading-tight">
      <div className="text-[13px] font-medium truncate max-w-[220px]">{top || "—"}</div>
      <div className="text-[10.5px] text-foreground/55 truncate max-w-[220px]">{sub || ""}</div>
    </div>
  );
}

function StatusPill({ v }) {
  const map = {
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    paid:      "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending:   "bg-amber-50 text-amber-800 border-amber-200",
    failed:    "bg-rose-50 text-rose-700 border-rose-200",
    cancelled: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${map[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {v || "—"}
    </span>
  );
}

function KindPill({ k }) {
  const map = {
    added:       "bg-emerald-50 text-emerald-700 border-emerald-200",
    deleted:     "bg-rose-50 text-rose-700 border-rose-200",
    set_default: "bg-blue-50 text-blue-700 border-blue-200",
  };
  const I = { added: Plus, deleted: Trash2, set_default: Star }[k];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] border ${map[k] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {I && <I className="w-2.5 h-2.5"/>}{k?.replace(/_/g, " ") || "—"}
    </span>
  );
}
